'use client'

import { motion } from 'framer-motion'
import { useState } from 'react'
import { FaInstagram, FaTiktok, FaYoutube, FaFacebookF, FaArrowRight, FaAppStore, FaCheck, FaEnvelope } from 'react-icons/fa'
import { sendEmail } from '@/utils/emailjs'
import Breadcrumbs from '@/app/components/Breadcrumbs'
import AppStoreLink from '@/app/components/AppStoreLink'
import Link from 'next/link';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
}

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
}

export default function ContactContent() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')

    const emailParams = {
      from_name: formData.name,
      reply_to: formData.email,
      subject: formData.subject,
      message: formData.message,
      type: 'contact_form' as const
    }

    try {
      console.log('Submitting contact form:', emailParams);
      const result = await sendEmail(emailParams)
      if (result.success) {
        console.log('Contact form submission successful');
        setSubmitStatus('success')
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        })
      } else {
        console.error('Contact form submission failed:', result.error);
        setSubmitStatus('error')
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="frame pt-6">
        <div className="w-full">
          <Breadcrumbs items={[{ label: 'Contact' }]} />
        </div>
      </div>
      <div className="frame pt-6 pb-12 md:pb-16">
        <motion.div
          className="w-full"
          initial="initial"
          animate="animate"
          variants={stagger}
        >
          <motion.div className="text-center mb-8 md:mb-12" variants={fadeInUp}>
            <h1 
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 font-display text-ink"
            >
              Get in Touch
            </h1>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Have questions about microplastics or our app? We&rsquo;re here to help you make informed decisions for a healthier future.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 sm:gap-8 md:gap-12">
            <motion.div
              className="md:col-span-3 bg-white rounded-2xl shadow-lg p-5 sm:p-6 md:p-8"
              variants={fadeInUp}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    autoComplete="name"
                    className="w-full px-4 py-3 text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-forest focus:border-transparent transition-[color,background-color,border-color,text-decoration-color] duration-200"
                    placeholder="Enter your name"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                    inputMode="email"
                    className="w-full px-4 py-3 text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-forest focus:border-transparent transition-[color,background-color,border-color,text-decoration-color] duration-200"
                    placeholder="Enter your email"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-forest focus:border-transparent transition-[color,background-color,border-color,text-decoration-color] duration-200"
                    placeholder="What's this about?"
                    required
                  />
                  {/* Brand enquiries have a dedicated intake with published
                      criteria and a fee schedule, so route them there rather
                      than letting them arrive as unstructured email. */}
                  <p className="mt-2 text-sm text-gray-600">
                    Representing a brand? Applications for the{' '}
                    <Link
                      href="/certification"
                      className="text-forest underline font-medium"
                    >
                      Plastic-Checked certification
                    </Link>{' '}
                    go through their own form, which captures the product scope
                    we need to assess.
                  </p>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className="w-full px-4 py-3 text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-forest focus:border-transparent transition-[color,background-color,border-color,text-decoration-color] duration-200"
                    placeholder="Tell us how we can help..."
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full bg-forest text-white min-h-[48px] py-3 px-6 rounded-control hover:bg-forest/90 transition-[color,background-color,border-color,text-decoration-color] duration-200 flex items-center justify-center gap-2 group focus:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 ${
                    isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
                  } pressable-sm`}
                >
                  {isSubmitting ? (
                    <span>Sending...</span>
                  ) : submitStatus === 'success' ? (
                    <>
                      <span>Message Sent</span>
                      <FaCheck className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      <span>Send Message</span>
                      <FaArrowRight className="w-4 h-4 group-hoverable:translate-x-1 transition-transform duration-200" />
                    </>
                  )}
                </button>
                {submitStatus === 'error' && (
                  <p className="text-red-500 text-sm text-center">
                    Failed to send message. Please try again later.
                  </p>
                )}
              </form>
            </motion.div>

            <motion.div 
              className="md:col-span-2 space-y-8"
              variants={fadeInUp}
            >
              <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
                <h2 className="text-xl font-semibold mb-6 text-forest">Connect With Us</h2>
                <div className="space-y-6">
                  <a
                    href="mailto:team@microplasticsapp.com"
                    className="flex items-center gap-4 text-gray-600 hover:text-forest transition-colors duration-200 group"
                  >
                    <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center group-hover:bg-forest/20 transition-colors duration-200">
                      <FaEnvelope className="w-6 h-6 text-forest" />
                    </div>
                    <div>
                      <h3 className="font-medium">Email</h3>
                      <p className="text-sm text-gray-500">team@microplasticsapp.com</p>
                    </div>
                  </a>
                  <a
                    href="https://www.instagram.com/microplastics.app/"
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 text-gray-600 hover:text-forest transition-colors duration-200 group"
                  >
                    <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center group-hover:bg-forest/20 transition-colors duration-200">
                      <FaInstagram className="w-6 h-6 text-[#E4405F]" />
                    </div>
                    <div>
                      <h3 className="font-medium">Instagram</h3>
                      <p className="text-sm text-gray-500">@microplastics.app</p>
                    </div>
                  </a>
                  <a
                    href="https://tiktok.com/@microplastics.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 text-gray-600 hover:text-forest transition-colors duration-200 group"
                  >
                    <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center group-hover:bg-forest/20 transition-colors duration-200">
                      <FaTiktok className="w-6 h-6 text-[#010101]" />
                    </div>
                    <div>
                      <h3 className="font-medium">TikTok</h3>
                      <p className="text-sm text-gray-500">@microplastics.app</p>
                    </div>
                  </a>
                  <a
                    href="https://www.youtube.com/channel/UCsV3ayImsxaXO-SFSwYS-3g"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 text-gray-600 hover:text-forest transition-colors duration-200 group"
                  >
                    <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center group-hover:bg-forest/20 transition-colors duration-200">
                      <FaYoutube className="w-6 h-6 text-[#FF0000]" />
                    </div>
                    <div>
                      <h3 className="font-medium">YouTube</h3>
                      <p className="text-sm text-gray-500">Subscribe for research explainers</p>
                    </div>
                  </a>
                  <a
                    href="https://www.facebook.com/profile.php?id=61591556934886"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 text-gray-600 hover:text-forest transition-colors duration-200 group"
                  >
                    <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center group-hover:bg-forest/20 transition-colors duration-200">
                      <FaFacebookF className="w-6 h-6 text-[#1877F2]" />
                    </div>
                    <div>
                      <h3 className="font-medium">Facebook</h3>
                      <p className="text-sm text-gray-500">Follow for updates &amp; tips</p>
                    </div>
                  </a>
                  <AppStoreLink
                    source="contact_connect_card"
                    className="flex items-center gap-4 text-gray-600 hover:text-forest transition-colors duration-200 group"
                  >
                    <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center group-hover:bg-forest/20 transition-colors duration-200">
                      <FaAppStore className="w-6 h-6" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="font-medium">App Store</h3>
                      <p className="text-sm text-gray-500">Available on iOS</p>
                    </div>
                  </AppStoreLink>
                </div>
              </div>

              <div className="bg-deepForest rounded-2xl p-6 md:p-8 text-white">
                <h2 className="text-xl font-semibold mb-4">Join Our Community</h2>
                <p className="mb-6 text-white/90">
                  Follow us on social media to stay updated on the latest research, tips, and community initiatives in the fight against microplastic pollution.
                </p>
                <div className="flex gap-4">
                  <a
                    href="https://www.instagram.com/microplastics.app/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="MicroPlastics on Instagram"
                    className="bg-white/20 hover:bg-white/30 transition-colors duration-200 p-3 rounded-xl"
                  >
                    <FaInstagram className="w-6 h-6" />
                  </a>
                  <a
                    href="https://tiktok.com/@microplastics.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="MicroPlastics on TikTok"
                    className="bg-white/20 hover:bg-white/30 transition-colors duration-200 p-3 rounded-xl"
                  >
                    <FaTiktok className="w-6 h-6" />
                  </a>
                  <a
                    href="https://www.youtube.com/channel/UCsV3ayImsxaXO-SFSwYS-3g"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="MicroPlastics on YouTube"
                    className="bg-white/20 hover:bg-white/30 transition-colors duration-200 p-3 rounded-xl"
                  >
                    <FaYoutube className="w-6 h-6" />
                  </a>
                  <a
                    href="https://www.facebook.com/profile.php?id=61591556934886"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="MicroPlastics on Facebook"
                    className="bg-white/20 hover:bg-white/30 transition-colors duration-200 p-3 rounded-xl"
                  >
                    <FaFacebookF className="w-6 h-6" />
                  </a>
                  <AppStoreLink
                    source="contact_community_icon"
                    aria-label="Download MicroPlastics on the App Store"
                    className="bg-white/20 hover:bg-white/30 transition-colors duration-200 p-3 rounded-xl"
                  >
                    <FaAppStore className="w-6 h-6" aria-hidden="true" />
                  </AppStoreLink>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  )
} 